/**
 * KPI Calculation utilities for booking management system
 * This file centralizes all KPI calculations and color coding rules
 */

// KPI Rules configuration - can be modified easily or pulled from API in the future
export const KPI_RULES = {
  prealertaKpi: {
    name: "Prealerta KPI",
    description: "ETA Date vs Alta Date",
    thresholds: [
      { value: 7, color: "text-green-500", bgColor: "bg-green-100" },
      { value: 5, color: "text-yellow-500", bgColor: "bg-yellow-100" },
      { default: true, color: "text-red-500", bgColor: "bg-red-100" },
    ],
  },
  altaPisoKpi: {
    name: "Alta vs Piso",
    description: "Piso Date vs Alta Date",
    thresholds: [
      { max: 10, color: "text-green-500", bgColor: "bg-green-100" },
      { min: 11, max: 15, color: "text-yellow-500", bgColor: "bg-yellow-100" },
      { default: true, color: "text-red-500", bgColor: "bg-red-100" },
    ],
  },
  altaAtracoKpi: {
    name: "Alta vs Atraco",
    description: "Atraco Date vs Alta Date",
    thresholds: [
      { max: 10, color: "text-green-500", bgColor: "bg-green-100" },
      { min: 11, max: 15, color: "text-yellow-500", bgColor: "bg-yellow-100" },
      { default: true, color: "text-red-500", bgColor: "bg-red-100" },
    ],
  },
  altaGlosaKpi: {
    name: "Alta vs Glosa",
    description: "Glosa Date vs Atraco Date",
    thresholds: [
      { max: 10, color: "text-green-500", bgColor: "bg-green-100" },
      { min: 11, max: 15, color: "text-yellow-500", bgColor: "bg-yellow-100" },
      { default: true, color: "text-red-500", bgColor: "bg-red-100" },
    ],
  },
};

/**
 * Calculates the difference in days between two dates
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number|null} - Difference in days or null if dates are invalid
 */
export const calculateDaysDifference = (date1, date2) => {
  if (!date1 || !date2) return null;

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Check for invalid dates
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;

  // Calculate the difference in milliseconds
  const differenceMs = Math.abs(d1 - d2);

  // Convert to days
  return Math.floor(differenceMs / (1000 * 60 * 60 * 24));
};

/**
 * Get the color for a KPI based on its value and threshold rules
 * @param {number} value - The KPI value
 * @param {Array} thresholds - Array of threshold rules
 * @returns {Object} - Color class and background color class
 */
export const getKpiColorFromThresholds = (value, thresholds) => {
  if (value === null) return { color: "text-gray-500", bgColor: "bg-gray-100" };

  // Find the matching threshold
  for (const threshold of thresholds) {
    // Handle threshold with exact value (>=)
    if ("value" in threshold && value >= threshold.value) {
      return { color: threshold.color, bgColor: threshold.bgColor };
    }

    // Handle threshold with range
    if (
      "min" in threshold &&
      "max" in threshold &&
      value >= threshold.min &&
      value <= threshold.max
    ) {
      return { color: threshold.color, bgColor: threshold.bgColor };
    }

    // Handle threshold with only max
    if (!("min" in threshold) && "max" in threshold && value <= threshold.max) {
      return { color: threshold.color, bgColor: threshold.bgColor };
    }

    // Handle threshold with only min
    if ("min" in threshold && !("max" in threshold) && value >= threshold.min) {
      return { color: threshold.color, bgColor: threshold.bgColor };
    }
  }

  // Find the default threshold
  const defaultThreshold = thresholds.find((t) => t.default);
  if (defaultThreshold) {
    return { color: defaultThreshold.color, bgColor: defaultThreshold.bgColor };
  }

  // Fallback
  return { color: "text-gray-500", bgColor: "bg-gray-100" };
};

/**
 * Calculate Prealerta KPI (ETA vs Alta)
 * @param {Object} booking - Booking object
 * @returns {Object} - KPI calculation result
 */
export const calculatePrealertaKpi = (booking) => {
  const etaDate =
    booking?.fechaETA && booking.fechaETA.length > 0
      ? booking.fechaETA[booking.fechaETA.length - 1].fechaETA
      : null;

  const altaDate = booking?.fechaAlta;
  const days = calculateDaysDifference(etaDate, altaDate);
  const { color, bgColor } = getKpiColorFromThresholds(
    days,
    KPI_RULES.prealertaKpi.thresholds
  );

  return {
    value: days,
    color,
    bgColor,
    label: days !== null ? `${days} días` : "-",
  };
};

/**
 * Calculate Alta vs Piso KPI
 * @param {Object} booking - Booking object
 * @returns {Object} - KPI calculation result
 */
export const calculateAltaPisoKpi = (booking) => {
  const days = calculateDaysDifference(booking?.fechaPiso, booking?.fechaAlta);
  const { color, bgColor } = getKpiColorFromThresholds(
    days,
    KPI_RULES.altaPisoKpi.thresholds
  );

  return {
    value: days,
    color,
    bgColor,
    label: days !== null ? `${days} días` : "-",
  };
};

/**
 * Calculate Alta vs Atraco KPI
 * @param {Object} booking - Booking object
 * @returns {Object} - KPI calculation result
 */
export const calculateAltaAtracoKpi = (booking) => {
  const days = calculateDaysDifference(
    booking?.fechaAtraco,
    booking?.fechaAlta
  );
  const { color, bgColor } = getKpiColorFromThresholds(
    days,
    KPI_RULES.altaAtracoKpi.thresholds
  );

  return {
    value: days,
    color,
    bgColor,
    label: days !== null ? `${days} días` : "-",
  };
};

/**
 * Calculate Alta vs Glosa KPI
 * @param {Object} booking - Booking object
 * @returns {Object} - KPI calculation result
 */
export const calculateAltaGlosaKpi = (booking) => {
  const days = calculateDaysDifference(
    booking?.fechaGlosa,
    booking?.fechaAtraco
  );
  const { color, bgColor } = getKpiColorFromThresholds(
    days,
    KPI_RULES.altaGlosaKpi.thresholds
  );

  return {
    value: days,
    color,
    bgColor,
    label: days !== null ? `${days} días` : "-",
  };
};

/**
 * Calculate all KPIs for a booking
 * @param {Object} booking - Booking object
 * @returns {Object} - All KPIs for the booking
 */
export const calculateAllKpis = (booking) => {
  return {
    prealertaKpi: calculatePrealertaKpi(booking),
    altaPisoKpi: calculateAltaPisoKpi(booking),
    altaAtracoKpi: calculateAltaAtracoKpi(booking),
    altaGlosaKpi: calculateAltaGlosaKpi(booking),
  };
};
